import React, {useEffect, useMemo, useState} from 'react'
import {useNavigate}                         from 'react-router-dom'
import {Box, Button, Chip, CircularProgress, Typography, useMediaQuery, useTheme} from '@mui/material'
import {ArrowForward, CircleOutlined}        from '@mui/icons-material'
import {Helmet}                              from 'react-helmet-async'
import dayjs                                 from 'dayjs'
import {doGet}                               from '../../util/do-fetch'
import {isExpired}                           from '../../util/prepkit-utils'
import {logger}                              from '../../util/log-utils'

const log = logger('PrepKits', 1)

/*
 * "My PrepKits" (091), rebuilt on the same design tokens as the dashboard and question screens:
 * surface cards with a hairline border and the category colour on the top edge, the token type
 * scale, and the same hover lift. It previously used raw MUI Cards and h2 headings, so arriving
 * here from the dashboard felt like leaving the product.
 *
 * The list is ALWAYS shown. An earlier version auto-opened the dashboard when the user had exactly
 * one active PrepKit, which meant /prepkit sometimes rendered and sometimes redirected -- and a
 * user who wanted the list (to renew a lapsed kit, or just to see what they have) could not reach
 * it. Landing somewhere other than where you navigated is worth more than the one saved click.
 */

const formatDate = (value) => (value ? dayjs(value).format('MMM D, YYYY') : '')

const PrepKits = () => {
	const theme = useTheme()
	const custom = theme.custom
	const navigate = useNavigate()
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

	const [prepKits, setPrepKits] = useState(null)

	useEffect(() => {
		const fetchPrepKits = async () => {
			try {
				const response = await doGet('/mini/prepkit')
				const data = await response.json()
				setPrepKits(Array.isArray(data) ? data : [])
			}
			catch (err) {
				log.debug('Could not load prepkits', err)
				setPrepKits([])
			}
		}
		fetchPrepKits()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	/*
	 * A clock, so a kit moves to Expired the second it runs out (193).
	 *
	 * isExpired() reads Date.now() internally, but this memo was keyed on [prepKits] alone -- so
	 * nothing re-ran it, and a kit that lapsed while the list was open stayed under Active, with a
	 * working View button, until the page was reloaded. The dashboard and the question screen both
	 * tick (useExpiryCountdown); this screen did not, which is the whole of why the three disagreed.
	 */
	const [now, setNow] = useState(() => Date.now())

	useEffect(() => {
		const interval = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(interval)
	}, [])

	const {active, expired} = useMemo(() => {
		const kits = prepKits || []

		return {
			active: kits.filter(pk => !isExpired(pk)),
			// most recently lapsed first -- that is the one most likely to be extended
			expired: kits
				.filter(pk => isExpired(pk))
				.sort((a, b) => new Date(b.expires_at) - new Date(a.expires_at)),
		}
		// `now` is not read in here -- it is the tick that makes isExpired() re-evaluate
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [prepKits, now])

	if (!prepKits) {
		return (
			<Box display="flex" justifyContent="center" py={10}>
				<CircularProgress/>
			</Box>
		)
	}

	const hasAny = prepKits.length > 0

	/*
	 * The page paints no background of its own. AppLayout centres non-study content, so a Box
	 * that fills with bg.main shrinks to its content and renders as a navy panel floating on the
	 * app background rather than as the page itself. Every other screen inherits the background
	 * set on html in App.js; this one now does too.
	 */
	return (
		<Box sx={{minHeight: '100%'}}>
			<Helmet><title>PrepKits - Birdsy</title></Helmet>

			{/*
			  * Narrower than the dashboard's 1280/1440 on purpose: this page is a short list of
			  * cards, and at full dashboard width a single active PrepKit stretched into a band
			  * across the screen.
			  */}
			<Box sx={{
				width: {xs: '100%', md: 900},
				maxWidth: '100%',
				mx: 'auto',
				px: {xs: 2, sm: 3, lg: 4},
				pt: isSmallScreen ? 1.5 : 2.5,
				pb: isSmallScreen ? 2 : 4,
			}}>
				{/* same greeting-header treatment as the dashboard (073) */}
				<Box sx={{mb: {xs: 3, md: 4}}}>
					<Typography sx={{
						fontSize: {xs: 24, md: 28},
						fontWeight: custom.fontWeight.bold,
						color: custom.text.main,
						lineHeight: 1.25,
						letterSpacing: '-0.02em',
					}}>
						{hasAny ? 'Your PrepKits' : 'No PrepKits yet'}
					</Typography>
					<Typography sx={{fontSize: custom.fontSize.md, color: custom.text.muted, mt: 0.5}}>
						{hasAny
							? 'Pick up where you left off, or extend one that has run out.'
							: 'Browse the catalogue to get started.'}
					</Typography>
				</Box>

				{active.length > 0 && (
					<>
						<SectionLabel custom={custom}>
							{active.length === 1 ? 'Active' : `Active (${active.length})`}
						</SectionLabel>
						<Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4}}>
							{active.map(pk => (
								<ActiveCard key={pk.id} prepKit={pk} custom={custom}
								            onClick={() => navigate(String(pk.id))}/>
							))}
						</Box>
					</>
				)}

				{expired.length > 0 && (
					<>
						<SectionLabel custom={custom}>
							{expired.length === 1 ? 'Expired' : `Expired (${expired.length})`}
						</SectionLabel>
						<Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
							{expired.map(pk => (
								<ExpiredCard key={pk.id} prepKit={pk} custom={custom}
								             onExtend={() => navigate(String(pk.id))}/>
							))}
						</Box>
					</>
				)}

				<Box sx={{mt: 4}}>
					<Button
						variant={hasAny ? 'text' : 'contained'}
						onClick={() => navigate('/prep')}
						endIcon={<ArrowForward/>}
						sx={{
							textTransform: 'none',
							borderRadius: `${custom.radius.sm}px`,
							fontWeight: custom.fontWeight.semibold,
							...(hasAny ? {color: custom.link} : {
								background: custom.indigo.primary,
								color: '#fff',
								px: 3,
								py: 1.25,
								'&:hover': {background: custom.indigo.hover},
							}),
						}}
					>
						Browse PrepKits
					</Button>
				</Box>
			</Box>
		</Box>
	)
}

const SectionLabel = ({children, custom}) => (
	<Typography sx={{
		fontSize: custom.fontSize.sm,
		fontWeight: custom.fontWeight.semibold,
		textTransform: 'uppercase',
		letterSpacing: '0.08em',
		color: custom.text.muted,
		mb: 1,
	}}>
		{children}
	</Typography>
)

/*
 * The primary object on the page: full card, category colour on the top edge, hover lift. Sized to
 * sit two-up on a wide screen and full width below that.
 */
const ActiveCard = ({prepKit, custom, onClick}) => (
	<Box
		data-ga="prepkit-card"
		onClick={onClick}
		sx={{
			flex: '1 1 420px',
			minWidth: 280,
			display: 'flex',
			alignItems: 'center',
			gap: 2,
			/*
			  * Outline only. These cards used to be filled with bg.surface, which in dark is
			  * #1A1C2E against a #0F101A page -- a blue-violet block that read as a colour choice
			  * nobody had made on purpose. The border and the category edge carry the shape; the
			  * card sits on the page rather than floating on a tinted panel.
			  */
			background: 'transparent',
			border: `1px solid ${custom.border.color}`,
			borderTop: `3px solid ${(prepKit.category && prepKit.category.color) || custom.indigo.primary}`,
			borderRadius: `${custom.radius.lg}px`,
			// invisible in dark; in light it is what separates a white card from a white page
			boxShadow: custom.shadow.card,
			p: 2.5,
			cursor: 'pointer',
			transition: 'transform 150ms, box-shadow 150ms, border-color 150ms',
			// hover lifts and strengthens the outline -- it must NOT reintroduce the fill
			'&:hover': {
				borderColor: custom.border.strong,
				boxShadow: custom.shadow.pop,
				transform: 'translateY(-2px)',
			},
		}}
	>
		<CircleOutlined sx={{fontSize: 28, color: (prepKit.category && prepKit.category.color) || custom.indigo.primary}}/>
		<Box sx={{minWidth: 0, flex: 1}}>
			<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
				<Typography sx={{
					fontSize: custom.fontSize.display,
					fontWeight: custom.fontWeight.bold,
					color: custom.text.main,
					lineHeight: 1.2,
				}}>
					{prepKit.name}
				</Typography>
				{prepKit.is_trial && (
					<Chip label="Trial" size="small" sx={{
						height: 20,
						fontSize: custom.fontSize.micro,
						fontWeight: custom.fontWeight.semibold,
						backgroundColor: custom.chip.bg,
						color: custom.chip.text,
						border: `1px solid ${custom.chip.border}`,
					}}/>
				)}
			</Box>
			<Typography sx={{fontSize: custom.fontSize.sm, color: custom.text.muted, mt: .25}}>
				Expires {formatDate(prepKit.expires_at)}
			</Typography>
		</Box>
		<ArrowForward sx={{color: custom.text.muted}}/>
	</Box>
)

/*
 * An expired kit. Flat, no shadow, muted -- clearly secondary to anything active -- but a full row
 * with its own Extend button.
 *
 * ONE action, not two (191). Extend goes to /prepkit/{id}, which is already the extend flow for a
 * lapsed kit -- PrepKit.js opens ExtendOverlay with variant="expired" on arrival. A separate View
 * button led to exactly the same place, so the row was offering one destination under two labels.
 *
 * EVERY expired kit renders this way. Previously only the most recently lapsed one did, and the
 * rest collapsed into thin text lines with a link; two kits that had both simply run out looked
 * like two different kinds of thing, and which one you got depended on the order they expired in.
 */
const ExpiredCard = ({prepKit, custom, onExtend}) => (
	<Box sx={{
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		flexWrap: 'wrap',
		background: 'transparent',
		border: `1px solid ${custom.border.color}`,
		borderRadius: `${custom.radius.md}px`,
		p: 2,
	}}>
		<Box sx={{minWidth: 0, flex: '1 1 240px'}}>
			<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
				<Typography sx={{
					fontSize: custom.fontSize.lg,
					fontWeight: custom.fontWeight.semibold,
					color: custom.text.main,
				}}>
					{prepKit.name}
				</Typography>
				{prepKit.is_trial && (
					<Chip label="Trial" size="small" sx={{
						height: 18,
						fontSize: custom.fontSize.micro,
						backgroundColor: custom.chip.bg,
						color: custom.chip.text,
						border: `1px solid ${custom.chip.border}`,
					}}/>
				)}
			</Box>
			<Typography sx={{fontSize: custom.fontSize.sm, color: custom.status.danger, mt: .25}}>
				Expired {formatDate(prepKit.expires_at)}
			</Typography>
		</Box>

		<Box display="flex" gap={1}>
			<Button onClick={onExtend} sx={{
				textTransform: 'none',
				borderRadius: `${custom.radius.sm}px`,
				background: custom.indigo.primary,
				color: '#fff',
				px: 2.5,
				fontSize: custom.fontSize.sm,
				fontWeight: custom.fontWeight.semibold,
				'&:hover': {background: custom.indigo.hover},
			}}>
				Extend
			</Button>
		</Box>
	</Box>
)

export default PrepKits
